/* import __COLOCATED_TEMPLATE__ from './phone-overview.hbs'; */
/* RESPONSIBLE TEAM: team-ai-agent */
import type AppService from 'embercom/services/app-service';
import Component from '@glimmer/component';
import { inject as service } from '@ember/service';
import type IntlService from 'embercom/services/intl';
import { tracked } from '@glimmer/tracking';
import { action } from '@ember/object';
import type FinVoiceService from 'embercom/services/fin-voice-service';
import type RouterService from '@ember/routing/router-service';
import { registerDestructor } from '@ember/destroyable';
import safeWindowOpen from 'embercom/lib/safe-window-open';
import type FinVoiceNumberConfiguration from 'embercom/models/fin-voice/number-configuration';
import type FinVoiceConfigService from 'embercom/services/fin-voice-config-service';

export default class FinAIAgentDeployPhoneOverview extends Component {
  readonly VOICE_PLAYGROUND_TAB = 'voice-playground';
  readonly DEPLOY_TAB = 'deploy';

  @service declare intl: IntlService;
  @service declare appService: AppService;
  @service declare finVoiceService: FinVoiceService;
  @service declare router: RouterService;
  @service declare finVoiceConfigService: FinVoiceConfigService;
  @service declare notificationsService: $TSFixMe;

  @tracked activeTab: typeof this.VOICE_PLAYGROUND_TAB | typeof this.DEPLOY_TAB =
    this.VOICE_PLAYGROUND_TAB;

  @tracked isSideMenuOpen =
    this.activeTab === this.VOICE_PLAYGROUND_TAB && this.canUseFinVoicePlaygroundDev;

  @tracked showNewLineModal = false;
  @tracked showPhoneSettingsSideMenu = false;
  @tracked selectedPhoneNumberId: string | undefined = undefined;
  @tracked selectedNumberConfiguration: FinVoiceNumberConfiguration | undefined = undefined;

  constructor(owner: unknown, args: never) {
    super(owner, args);
    this._loadCorrectTab();

    this.router.on('routeDidChange', this._handleRouteChange);

    registerDestructor(this, () => {
      this.router.off('routeDidChange', this._handleRouteChange);
    });
  }

  get canUseFinVoicePlaygroundDev() {
    return this.appService.app.canUseFinVoicePlaygroundDev;
  }

  get showSideMenu() {
    return this.activeTab === this.VOICE_PLAYGROUND_TAB && this.isSideMenuOpen;
  }

  get showSideMenuToggle() {
    return this.activeTab === this.VOICE_PLAYGROUND_TAB;
  }

  get sessions() {
    return this.finVoiceService.sessions;
  }

  get hasSessions() {
    return !!this.sessions?.length;
  }

  get verboseLogsEnabled() {
    return this.finVoiceService.verboseLogsEnabled;
  }

  get title() {
    return this.intl.t('ai-agent.new-submenu.phone');
  }

  get canUseNewVoiceUI() {
    return this.appService.app.canUseNewVoiceUI;
  }

  get selectedPhoneNumber() {
    if (!this.selectedPhoneNumberId) {
      return undefined;
    }

    return this.finVoiceConfigService.phoneNumbers[this.selectedPhoneNumberId];
  }

  get isExternalProvider() {
    return Boolean(this.selectedPhoneNumber?.isExternalProvider);
  }

  @action
  _handleRouteChange() {
    this._loadCorrectTab();
  }

  _loadCorrectTab() {
    let currentTabParam = this.router.currentRoute?.queryParams?.tab;

    if (!currentTabParam) {
      return;
    }

    if (currentTabParam === this.VOICE_PLAYGROUND_TAB) {
      this.activeTab = this.VOICE_PLAYGROUND_TAB;
    } else if (currentTabParam === this.DEPLOY_TAB) {
      this.activeTab = this.DEPLOY_TAB;
    }
  }

  @action
  closeSideMenu() {
    this.isSideMenuOpen = false;
  }

  @action
  openSideMenu() {
    this.isSideMenuOpen = true;
  }

  @action
  reportIssue() {
    return this.finVoiceService.reportIssue();
  }

  @action
  copyLatestSessionId() {
    return this.finVoiceService.copyLatestSessionId();
  }

  @action
  toggleVerboseLogsEnabled() {
    this.finVoiceService.verboseLogsEnabled = !this.finVoiceService.verboseLogsEnabled;
  }

  @action
  changeTab(tab: typeof this.VOICE_PLAYGROUND_TAB | typeof this.DEPLOY_TAB) {
    this.activeTab = tab;
    this.router.transitionTo({ queryParams: { tab } });
  }

  @action
  openNewLineModal() {
    this.showNewLineModal = true;
  }

  @action
  closeNewLineModal() {
    this.showNewLineModal = false;
  }

  @action
  openHelpArticle() {
    safeWindowOpen(
      'https://www.intercom.com/help/en/articles/10697275-deploy-fin-ai-agent-over-phone',
    );
  }

  @action
  handleLineCreated() {
    this.showNewLineModal = false;
  }

  @action
  goToVoiceTesting() {
    this.router.transitionTo({ queryParams: { tab: 'voice-playground' } });
  }

  @action
  openPhoneSettingsSideMenu() {
    if (!this.selectedPhoneNumber) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return;
    }

    if (this.isExternalProvider) {
      this.showPhoneSettingsSideMenu = true;
    } else {
      this.router.transitionTo('apps.app.settings.channels.phone');
    }
  }

  @action
  closePhoneSettingsSideMenu() {
    this.showPhoneSettingsSideMenu = false;
  }

  @action
  setSelectedLine(phoneNumberId: string, numberConfiguration: FinVoiceNumberConfiguration) {
    if (!phoneNumberId || !numberConfiguration) {
      this.notificationsService.notifyError(
        this.intl.t('ai-agent.voice-playground.line-settings.phone-number-not-found'),
      );
      return;
    }

    this.selectedPhoneNumberId = phoneNumberId;
    this.selectedNumberConfiguration = numberConfiguration;
  }
}

declare module '@glint/environment-ember-loose/registry' {
  export default interface Registry {
    'AiAgent::Deploy::PhoneOverview': typeof FinAIAgentDeployPhoneOverview;
  }
}
